import {useCallback, useContext, useEffect, useRef, useState} from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import {useTranslation} from 'react-i18next'
import {useLeadSubmission} from 'slices/ProductTourSection/hooks/useLeadSubmission'
import {ButtonV2} from 'src/common/ButtonV2'
import Dialog from 'src/common/Dialog'
import {useGetConfig} from 'src/common/hooks/useGetConfig'
import {DialogContext} from 'src/context/DialogContext'
import {findFieldError} from '../helpers/findFieldError'

type LeadGenerationFieldProps = {
  pageUid: string
  placeholder?: string | null
  submitButtonText?: string | null
  onSuccess?: () => void
  buttonSize?: 'small' | 'medium'
  tracking: {
    recaptchaAction: string
    formName: string
    intent: string
    location: string
  }
  errorMessage?: string | null
}

export const LeadGenerationField = ({
  pageUid,
  placeholder,
  submitButtonText,
  onSuccess,
  buttonSize = 'small',
  tracking,
  errorMessage,
}: LeadGenerationFieldProps) => {
  const [email, setEmail] = useState('')
  const config = useGetConfig()
  const {i18n} = useTranslation()
  const {setIsDialogOpen} = useContext(DialogContext)!
  const recaptchaRef = useRef<ReCAPTCHA | null>(null)
  const [error, setError] = useState<string | null>(null)

  const callbackAfterSubmit = useCallback(() => {
    // Clear any errors on successful submission
    setError(null)

    if (onSuccess) {
      onSuccess()
    }

    // Reset form after successful submission
    setEmail('')
  }, [onSuccess])

  const callbackAfterError = useCallback(
    (message: string) => {
      setError(errorMessage || message || 'An error occurred')
    },
    [errorMessage],
  )

  const {submitLead, apiError} = useLeadSubmission({
    pageUid,
    onSuccess: callbackAfterSubmit,
    tracking,
    errorMessage: errorMessage || '',
    onError: callbackAfterError,
  })

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      // Clear any previous errors
      setError(null)

      if (email.trim()) {
        submitLead(email)
      }
    },
    [email, submitLead],
  )

  //check for captcha error and show reCaptcha
  useEffect(() => {
    if (!apiError || !apiError.response?.data?.fieldErrors) {
      return
    }

    const captchaError = findFieldError(apiError.response.data.fieldErrors, 'captchaToken')

    if (captchaError) {
      setIsDialogOpen(true)
    }
  }, [apiError, setIsDialogOpen])

  const handleChallengeResolved = useCallback(
    (token: string | null) => {
      if (!token) {
        return
      }

      submitLead(email, token)
      setIsDialogOpen(false)
      recaptchaRef.current?.reset()
    },
    [email, setIsDialogOpen, submitLead],
  )

  return (
    <>
      <div className="relative">
        <form
          onSubmit={handleSubmit}
          className="flex w-[318px] items-center justify-center gap-[4px] rounded-[6px] border border-moss-white/10 bg-moss-white p-[10px] pl-[14px] backdrop-blur-[10px]"
        >
          {/* Input Field */}
          <div className="flex min-h-px min-w-px grow items-center gap-[2px]">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={placeholder || ''}
              required
              className="text-body-bold w-full bg-transparent text-black placeholder:text-moss-grey-300 focus:outline-none"
            />
          </div>

          {/* CTA Button */}
          <ButtonV2
            variant="primary"
            type="submit"
            size={buttonSize}
            className="shrink-0 uppercase"
            trackingData={{
              location: tracking.location,
              dataType: 'submit-button',
              pageName: pageUid,
              label: submitButtonText || 'Book a demo',
            }}
          >
            {submitButtonText || 'Book a demo'}
          </ButtonV2>
        </form>
        {/* Error Message */}
        {error && <p className="text-tg-body-s absolute mt-2 text-ui-red-450">{error}</p>}
      </div>
      <Dialog dismissible>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={config.RECAPTCHA_V2}
          onChange={handleChallengeResolved}
          hl={i18n.language}
        />
      </Dialog>
    </>
  )
}
